<script setup lang="ts">
import { defineEmits } from 'vue';
import { useEquipment } from "@/composables/tasks/useEquipment";
import { useEquipmentForm } from "@/composables/tasks/useEquipmentForm";

// ===================================================================
// Emits
// ===================================================================
const emit = defineEmits(['cancel', 'saved']);

// ===================================================================
// Composables
// ===================================================================
// Equipment
const {
  getOptionLabel,
  isOptionModifiable,
} = useEquipment();

// Equipment Form
const {
  formState,
  selectedEquipmentType,
  handleSubmit,
} = useEquipmentForm();

// ===================================================================
// Actions
// ===================================================================
// Save new configuration
const saveNewConfig = async () => {
  await handleSubmit();
  emit('saved');
};

// Cancel adding
const cancelAddingConfig = () => {
  emit('cancel');
};
</script>

<template>
  <div class="border p-4 bg-gray-50 mb-6">
    <!-- Loading state -->
    <div v-if="!selectedEquipmentType && formState.selectedEquipmentTypeId" class="bg-orange-50 border border-orange-200 text-orange-700 p-4 rounded mb-4">
      <h3 class="font-medium">Loading Equipment Configuration...</h3>
      <p class="text-sm mt-2">Selected ID: {{ formState.selectedEquipmentTypeId }}</p>
    </div>

    <!-- Main form content -->
    <template v-if="selectedEquipmentType">
      <h2 class="text-lg font-semibold mb-4">
        Add New {{ selectedEquipmentType.name }} Configuration
      </h2>

      <!-- General error display -->
      <div v-if="Object.keys(formState.errors).length > 0" class="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4">
        <div v-for="(errors, field) in formState.errors" :key="field">
          <div v-for="(error, index) in errors" :key="index">{{ error }}</div>
        </div>
      </div>

      <div class="space-y-4">
        <div v-for="option in selectedEquipmentType.configOptions" :key="option.name"
             class="border-b pb-4">
          <label :for="`new-${option.name}`" class="block text-sm font-medium mb-2">
            {{ getOptionLabel(selectedEquipmentType.configOptions, option.name) }}
            <span v-if="option.required" class="text-red-500 ml-1">*</span>
          </label>

          <!-- Different input types based on option type -->
          <div>
            <!-- Text input -->
            <input v-if="option.type === 'text'"
                   :id="`new-${option.name}`"
                   v-model="formState.values[option.name]"
                   type="text"
                   class="border border-gray-300 px-3 py-2 w-full"
                   :disabled="formState.isSubmitting" />

            <!-- Number input -->
            <div v-else-if="option.type === 'number'" class="flex items-center">
              <input :id="`new-${option.name}`"
                     v-model.number="formState.values[option.name]"
                     type="number"
                     :step="option.name.toLowerCase().includes('needle') && option.name.toLowerCase().includes('size') ? '0.01' : 'any'"
                     class="border border-gray-300 px-3 py-2 w-full"
                     :disabled="formState.isSubmitting" />
              <span v-if="option.unit" class="ml-2 text-gray-500">{{ option.unit }}</span>
            </div>

            <!-- Select dropdown -->
            <select v-else-if="option.type === 'select'"
                    :id="`new-${option.name}`"
                    v-model="formState.values[option.name]"
                    class="border border-gray-300 px-3 py-2 w-full"
                    :disabled="formState.isSubmitting">
              <option v-for="opt in option.options || []" :key="opt" :value="opt">{{ opt }}</option>
            </select>

            <!-- Boolean toggle -->
            <div v-else-if="option.type === 'boolean'" class="flex items-center">
              <input :id="`new-${option.name}`"
                     v-model="formState.values[option.name]"
                     type="checkbox"
                     class="h-4 w-4 border-gray-300 rounded"
                     :disabled="formState.isSubmitting" />
              <label :for="`new-${option.name}`" class="ml-2 text-sm text-gray-600">
                {{ option.label || "Enabled" }}
              </label>
            </div>
          </div>

          <!-- Field errors -->
          <div v-if="formState.errors && formState.errors[option.name] && formState.errors[option.name].length > 0" class="mt-1 text-xs text-red-600">
            {{ formState.errors[option.name][0] }}
          </div>

          <!-- Justification notice -->
          <div v-if="option.require_justification_for_change && isOptionModifiable(option)"
               class="mt-1 text-xs text-amber-600">
            Note: Changes will require justification during task execution.
          </div>
        </div>
      </div>

      <div class="flex justify-end space-x-2 mt-6">
        <button @click="saveNewConfig"
                class="bg-green-500 text-white hover:bg-green-600 px-4 py-2 text-sm font-medium rounded"
                :disabled="formState.isSubmitting">
          {{ formState.isSubmitting ? 'Saving...' : 'Save Configuration' }}
        </button>
        <button @click="cancelAddingConfig"
                type="button"
                class="bg-gray-500 text-white hover:bg-gray-600 px-4 py-2 text-sm font-medium rounded"
                :disabled="formState.isSubmitting">
          Cancel
        </button>
      </div>
    </template>
  </div>
</template>